<template>
  <div :class="className || null" :style="containerStyle">
    <div v-if="headline || navPosition === 'top'" class="row card-slider-headline">
      <div class="col">
        <h2 v-if="headline">{{ headline }}</h2>
        <Controls v-if="useSwiper && navPosition === 'top'" :prev="prev" :next="next" />
      </div>
    </div>
    <swiper-container
        v-if="useSwiper"
        ref="swiper"
        class="card-slider-container card-deck deck-cols-md-2 deck-cols-lg-4 card-slider-with-fade"
        :slides-per-view="computedSlidesPerView"
        :loop="loop"
        :style="dynamicSwiperStyle"
    >
      <swiper-slide
          v-for="(card, index) in duplicatedCards"
          :key="index"
          class="card-slider-slide"
      >
        <CardContent :card="card" :style="swiperStyle" />
      </swiper-slide>
    </swiper-container>

    <div v-else class="card-deck deck-cols-md-2 deck-cols-lg-4">
      <CardContent
          v-for="(card, index) in cards"
          :key="index"
          :card="card"
      />
    </div>

    <Controls v-if="useSwiper && navPosition === 'bottom'" :prev="prev" :next="next" />
  </div>
</template>

<script>
import { register } from "swiper/element/bundle";
import "swiper/css";

register();

export default {
  name: "CardSlider",
  components: {
    CardContent: {
      props: {
        card: {
          type: Object,
          required: true,
        },
      },
      template: `
        <div class="card linked-card">
          <div class="card-body">
            <h2 class="card-title">{{ card.title }}</h2>
            <p class="card-text" style="min-height: 4.5rem;">{{ card.description }}</p>
          </div>
          <img
            width="100%"
            height="210"
            :src="card.image"
            :alt="card.alt"
            role="presentation"
            class="card-img-bottom"
            style="object-fit: cover;"
          />
          <a :href="card.link" class="stretched-link">
            <span class="card-hover-panel">
              <span class="link-label font-weight-medium">Mehr erfahren</span>
              <span aria-hidden="true" class="link-icon ml-2">
                <svg xmlns="http://www.w3.org/2000/svg" height="100%" viewBox="0 0 9 18">
                  <polygon
                      id="chevron_right"
                      fill="currentColor"
                      fill-rule="evenodd"
                      stroke="none"
                      points="0,2.198466 3.3416683,1.1368684e-13 9.4079139,9.2206932 3.3280121,18.218948 0.013656227,15.979518 4.5995008,9.190233"
                  ></polygon>
                </svg>
              </span>
            </span>
          </a>
        </div>
      `,
    },
    Controls: {
      props: {
        prev: {
          type: Function,
          required: true,
        },
        next: {
          type: Function,
          required: true,
        },
      },
      template: `
        <div class="card-slider-controls">
          <button class="card-slider-button-prev" aria-label="Vorheriger Slide" @click="prev">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="32" viewBox="0 0 9 18">
              <polygon
                  fill="currentColor"
                  fill-rule="evenodd"
                  stroke="none"
                  points="9.4,2.2 6.1,0 0,9.2 6.1,18 9.4,15.8 4.8,9.2"
              ></polygon>
            </svg>
          </button>

          <button class="card-slider-button-next" aria-label="Nächster Slide" @click="next">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="32" viewBox="0 0 9 18">
              <polygon
                  fill="currentColor"
                  fill-rule="evenodd"
                  stroke="none"
                  points="0,2.2 3.3,0 9.4,9.2 3.3,18 0,15.8 4.6,9.2"
              ></polygon>
            </svg>
          </button>
        </div>
      `
    }
  },
  props: {
    className: {
      type: String,
      default: "",
    },
    headline: {
      type: String,
      default: ""
    },
    loop: {
      type: Boolean,
      default: true,
    },
    cards: {
      type: Array,
      default: () => [],
    },
    useSwiper: {
      type: Boolean,
      default: true,
    },
    navPosition: {
      type: String,
      default: "bottom",
    }
  },
  data() {
    return {
      slidesPerView: this.getSlidesPerViewByBreakpoint(),
      swiperStyle: {},
      containerStyle: {},
    };
  },
  computed: {
    computedSlidesPerView() {
      return this.slidesPerView;
    },
    dynamicSwiperStyle() {
      if (this.className.includes("container-xl")) {
        if (this.slidesPerView > 3) {
          return { marginRight: "-27%" };
        } else if (this.slidesPerView === 3) {
          return { marginRight: "-55%" };
        } else if (this.slidesPerView === 2) {
          return { marginRight: "-75%" };
        } else {
          return {};
        }
      } else {
        if (this.slidesPerView > 3) {
          return { marginRight: "-10%" };
        } else if (this.slidesPerView === 3) {
          return { marginRight: "-46%" };
        } else if (this.slidesPerView === 2) {
          return { marginRight: "-63%" };
        } else {
          return {};
        }
      }
    },
    duplicatedCards() {
      const cardCount = this.cards.length;

      if (cardCount === 0) {
        return [];
      }

      if (cardCount === 5) {
        return [...this.cards, ...this.cards];
      }

      if (cardCount < 5) {
        const multiplier = Math.ceil(10 / cardCount);
        return Array(multiplier).fill(this.cards).flat().slice(0, 10);
      }

      return this.cards;
    },
  },
  methods: {
    next() {
      if (this.useSwiper) {
        this.$refs.swiper.swiper.slideNext();
      }
    },
    prev() {
      if (this.useSwiper) {
        this.$refs.swiper.swiper.slidePrev();
      }
    },
    getCssCustomProperty(property) {
      return getComputedStyle(document.documentElement).getPropertyValue(property).trim();
    },
    getSlidesPerViewByBreakpoint() {
      const breakpointLg = parseInt(this.getCssCustomProperty("--breakpoint-lg"), 10);
      const breakpointMd = parseInt(this.getCssCustomProperty("--breakpoint-md"), 10);

      if (window.innerWidth >= breakpointLg) {
        return 5;
      } else if (window.innerWidth >= breakpointMd) {
        return 3;
      } else {
        return 2;
      }
    },
    updateSlidesPerView() {
      this.slidesPerView = this.getSlidesPerViewByBreakpoint();
    },
    updateSwiperStyle() {
      const breakpointSm = parseInt(this.getCssCustomProperty("--breakpoint-sm"), 10);

      if (window.innerWidth <= breakpointSm) {
        this.swiperStyle = { marginLeft: "30px", marginRight: "0px" };
        this.containerStyle = { padding: "0px" };
      } else {
        this.swiperStyle = { marginLeft: "", marginRight: ""};
        this.containerStyle = { padding: "" };
      }
    },
  },
  mounted() {
    this.updateSlidesPerView();
    this.updateSwiperStyle();

    window.addEventListener("resize", () => {
      this.updateSlidesPerView();
      this.updateSwiperStyle();
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", () => {
      this.updateSlidesPerView();
      this.updateSwiperStyle();
    });
  },
};
</script>